// providers/redtail.ts
import * as Api from '@ViewModels';
import { complementSelect, number, provider, providerTag, select, toggle } from './helpers';

export const redtailConfig = provider(Api.IntegrationProvider.Redtail, {
	displayName: 'Redtail',

	configuration: {
		// Redtail allows multi-user sync -- no single connected user needs to be selected as the sync user
		showSyncUser: false,
	},

	// Redtail syncs via all connected users rather than a single designated sync user,
	// so every connected user counts as sync-enabled in the Connections view.
	isConnectionSyncEnabled: userConnection => userConnection.isConnected,

	main: {
		writebacks: true,
		fields: [
			toggle('preferNicknameAsFirstName', {
				label: 'Prefer Nickname as First Name',
				subtitle: 'Use the contact nickname from Redtail as the first name in Levitate',
			}),
			toggle('useForMeetingScheduling', {
				label: 'Use for Meeting Scheduling',
				subtitle: "Use Redtail's activity calendar for the meeting scheduler in Levitate",
			}),
			select(
				'prospectIntegration',
				[
					{ id: Api.ProspectIntegration.None, label: 'None' },
					{ id: Api.ProspectIntegration.All, label: 'All' },
					{ id: Api.ProspectIntegration.Timeline, label: 'Timeline' },
				],
				{
					label: 'Prospect Sync',
					subtitle: 'Controls whether prospect contacts are synced from Redtail',
					parentKey: 'prospectOptions',
				}
			),
			number('timelineYears', {
				label: 'Timeline Years',
				subtitle: 'Number of years of prospect history to sync',
				min: 1,
				max: 10,
				placeholder: 'Years',
				displaySuffix: 'year',
				parentKey: 'prospectOptions',
				visibleWhen: {
					key: 'prospectIntegration',
					parentKey: 'prospectOptions',
					value: Api.ProspectIntegration.Timeline,
				},
				isSubSetting: true,
			}),
			complementSelect('blockedStatuses', 'availableStatuses', {
				label: 'Allowed Statuses',
				subtitle: 'Select which Redtail statuses are allowed to load into Levitate',
			}),
			complementSelect('blockedCategories', 'availableCategories', {
				label: 'Allowed Categories',
				subtitle: 'Select which Redtail categories are allowed to load into Levitate',
			}),
			complementSelect('blockedContactTypes', 'availableContactTypes', {
				label: 'Allowed Contact Types',
				subtitle: 'Select which Redtail contact types are allowed to load into Levitate',
			}),
		],
	},

	keyFacts: {
		birthdayDisplay: true,
		extraFields: [
			select(
				'financialReviewSource',
				[
					{ id: Api.RedtailFinancialReviewSource.None, label: 'None' },
					{ id: Api.RedtailFinancialReviewSource.ClientSince, label: 'Client Since' },
					{ id: Api.RedtailFinancialReviewSource.DateOfBirth, label: 'Date of Birth' },
					{ id: Api.RedtailFinancialReviewSource.ClientReviewReminder, label: 'Client Review Reminder' },
				],
				{
					label: 'Financial Review Key Fact',
					subtitle: 'Source field used to derive the Financial Review key fact',
				}
			),
			toggle('familyMemberKeyFactsEnabled', {
				label: 'Family Member Key Facts',
				subtitle: 'Show family member relationships from Redtail as key facts',
			}),
			toggle('weddingAnniversaryKeyFactsEnabled', {
				label: 'Wedding Anniversary Key Facts',
				subtitle: 'Show wedding anniversaries from Redtail as key facts',
			}),
		],
	},

	tags: {
		coreTags: true,
		providerTags: [
			providerTag('Status', 'Status'),
			providerTag('Category', 'Category'),
			providerTag('Source', 'Source'),
			providerTag('Gender', 'Gender'),
			{
				id: 'Keywords',
				label: 'Keywords',
				settingsKey: 'keywordTagsEnabled',
			},
			{
				id: 'TagGroups',
				label: 'Tag Groups',
				settingsKey: 'tagGroupTagsEnabled',
			},
		],
	},
});
